import { useCallback, useEffect, useRef, useState } from "react";
import { checkDrugTestRequirements } from "../../scripts/api/bluecrew/job";

interface DrugTestRequirementsResult {
  drugTestRequired: boolean;
  exemptStates: string[];
  isLoadingDrugTest: boolean;
}

export const debouncedDrugTestRequirements = (
  skillsetId: number | undefined,
  featureFlagEnabled: boolean,
  delay: number = 300
): DrugTestRequirementsResult => {
  const [drugTestRequired, setDrugTestRequired] = useState<boolean>(false);
  const [exemptStates, setExemptStates] = useState<string[]>([]);
  const [isLoadingDrugTest, setIsLoadingDrugTest] = useState<boolean>(false);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastSkillsetIdRef = useRef<number | undefined>(undefined);

  const fetchDrugTestRequirements = useCallback(async (id: number, signal: AbortSignal) => {
    try {
      const response = await checkDrugTestRequirements(id, { signal });
      if (!signal.aborted) {
        setDrugTestRequired(response.requiresDrugScreen);
        setExemptStates(
          Array.isArray(response.exemptStates) && response.exemptStates.length > 0
            ? response.exemptStates
            : []
        );
      }
    } catch (error: any) {
      if (!signal.aborted && error.name !== 'AbortError') {
        setDrugTestRequired(false);
        setExemptStates([]);
      }
    } finally {
      if (!signal.aborted) {
        setIsLoadingDrugTest(false);
      }
    }
  }, []);

  const debouncedFetch = useCallback((id: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    setIsLoadingDrugTest(true);

    timeoutRef.current = setTimeout(() => {
      if (currentController === abortControllerRef.current && !currentController.signal.aborted) {
        fetchDrugTestRequirements(id, currentController.signal);
      }
    }, delay);
  }, [fetchDrugTestRequirements, delay]);

  useEffect(() => {
    if (!featureFlagEnabled) {
      setDrugTestRequired(false);
      setExemptStates([]);
      setIsLoadingDrugTest(false);
      return;
    }

    if (!skillsetId) {
      setDrugTestRequired(false);
      setExemptStates([]);
      setIsLoadingDrugTest(false);
      return;
    }

    if (lastSkillsetIdRef.current === skillsetId) {
      return;
    }

    lastSkillsetIdRef.current = skillsetId;
    debouncedFetch(skillsetId);

    // eslint-disable-next-line consistent-return
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [skillsetId, featureFlagEnabled, debouncedFetch]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    drugTestRequired,
    exemptStates,
    isLoadingDrugTest,
  };
};
